import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Zap, RefreshCw, Loader2, Power, PowerOff, Activity, Users } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { DashboardShell } from '../components/brand/DashboardShell';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ADMIN_NAV = [
  { icon: Activity, label: 'Operations Executive', href: '/operations/executive' },
  { icon: Users, label: 'Clinician Retention', href: '/admin/clinician-retention' },
  { icon: Zap, label: 'Surge Pricing', active: true },
];

export default function AdminSurgePricing() {
  const { getAuthHeader } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/surge/conditions`, getAuthHeader());
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load surge conditions');
    } finally { setLoading(false); }
  }, [getAuthHeader]);

  useEffect(() => { load(); }, [load]);

  const act = async (cell, on) => {
    try {
      const url = on ? '/admin/surge/enable' : '/admin/surge/disable';
      await axios.post(`${API}${url}`, {
        state: cell.state,
        service_line: cell.service_line,
        multiplier: cell.recommended_multiplier,
      }, getAuthHeader());
      toast.success(`Surge ${on ? 'enabled' : 'disabled'} for ${cell.state}/${cell.service_line}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not update surge');
    }
  };

  const k = data?.kpis || {};
  return (
    <DashboardShell
      navItems={ADMIN_NAV}
      title="Demand Surge Pricing"
      subtitle="Pay clinicians a transparent multiplier when demand spikes. Shortens referral SLA, lifts clinician retention."
      testId="admin-surge-pricing-page"
    >
      {loading && <div className="cn-card p-10 text-center"><Loader2 className="w-6 h-6 animate-spin text-cn-primary mx-auto" /></div>}
      {!loading && data && (
        <>
          <div className="cn-card p-4 mb-6 flex flex-wrap items-center gap-3">
            <Button onClick={load} className="cn-btn cn-btn-ghost !py-2" data-testid="surge-refresh">
              <RefreshCw className="w-4 h-4" /> Refresh
            </Button>
          </div>
          <div className="grid sm:grid-cols-3 gap-3 mb-6" data-testid="surge-kpis">
            <Tile label="Total cells" value={k.total_cells} testid="surge-kpi-total" />
            <Tile label="High-pressure cells" value={k.high_pressure_cells} tone={(k.high_pressure_cells || 0) > 0 ? 'red' : 'emerald'} testid="surge-kpi-highpressure" />
            <Tile label="Live surges" value={k.live_surges} tone="amber" testid="surge-kpi-live" />
          </div>
          <div className="cn-card overflow-hidden" data-testid="surge-table-card">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="surge-table">
                <thead className="bg-cn-bone-50 border-b border-cn-border text-[11px] uppercase tracking-wider font-bold text-cn-text-muted">
                  <tr className="text-left">
                    <th className="px-5 py-3">State / Line</th>
                    <th className="px-3 py-3 text-right">Open refs</th>
                    <th className="px-3 py-3 text-right">Capacity</th>
                    <th className="px-3 py-3 text-right">Pressure</th>
                    <th className="px-3 py-3 text-right">Recommended</th>
                    <th className="px-3 py-3">Status</th>
                    <th className="px-3 py-3 text-right">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-cn-border">
                  {(data.cells || []).map((c, idx) => (
                    <tr key={`${c.state}-${c.service_line}`} data-testid={`surge-row-${idx}`}>
                      <td className="px-5 py-2.5 font-medium">{c.state} · <span className="text-cn-text-muted">{c.service_line}</span></td>
                      <td className="px-3 py-2.5 text-right text-xs">{c.open_refs}</td>
                      <td className="px-3 py-2.5 text-right text-xs">{c.capacity_proxy}</td>
                      <td className={`px-3 py-2.5 text-right text-xs font-semibold ${c.pressure_pct >= 30 ? 'text-red-700' : c.pressure_pct >= 15 ? 'text-amber-700' : 'text-emerald-700'}`}>{c.pressure_pct}%</td>
                      <td className="px-3 py-2.5 text-right font-display font-bold">{c.recommended_multiplier}×</td>
                      <td className="px-3 py-2.5">
                        {c.active_surge ? (
                          <span className="text-[10px] px-2 py-0.5 rounded-full border font-semibold bg-amber-50 text-amber-700 border-amber-200">{c.active_surge.multiplier}× LIVE</span>
                        ) : (
                          <span className="text-[10px] text-cn-text-muted">off</span>
                        )}
                      </td>
                      <td className="px-3 py-2.5 text-right">
                        {c.active_surge ? (
                          <Button onClick={() => act(c, false)} className="cn-btn cn-btn-ghost !py-1 !px-2 text-[10px]" data-testid={`surge-disable-${idx}`}>
                            <PowerOff className="w-3 h-3" /> Disable
                          </Button>
                        ) : (
                          <Button onClick={() => act(c, true)} className="cn-btn cn-btn-primary !py-1 !px-2 text-[10px]" data-testid={`surge-enable-${idx}`}>
                            <Power className="w-3 h-3" /> Enable
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {(data.cells || []).length === 0 && (
              <div className="p-10 text-center text-sm text-cn-text-muted" data-testid="surge-empty">No active referrals to compute surge for.</div>
            )}
          </div>
        </>
      )}
    </DashboardShell>
  );
}

function Tile({ label, value, tone = 'neutral', testid }) {
  const cls = { neutral: 'text-cn-text', red: 'text-red-700', emerald: 'text-emerald-700', amber: 'text-amber-700' }[tone];
  return (
    <div className="cn-card p-4" data-testid={testid}>
      <p className="text-[10px] uppercase tracking-wider font-bold text-cn-text-muted">{label}</p>
      <p className={`font-display font-bold text-2xl ${cls}`}>{value ?? 0}</p>
    </div>
  );
}
